// OnboardingTour — 3-sesi state machine untuk first-time UX (Doc 09 §15.1).
// Persist sessionCount ke IndexedDB settings (reuse ProjectRepository.setSetting).
// Sesi 3 di-stub sampai M6 Phase 6.B selesai (cek shareLinkEnabled setting).

import { STRINGS } from '@tm/shared'
import { trackEvent } from '../analytics/plausible.js'

const MAX_SESSIONS = 3

const SESI_TEMPLATE = [
  // Sesi 1: "Bikin game pertama"
  {
    key: 'sesi1-title',
    title: STRINGS.onboardingSesi1Title,
    body: STRINGS.onboardingSesi1Body,
    cta: STRINGS.onboardingSesi1Cta,
  },
  // Sesi 2: "Tambah blok sendiri"
  {
    key: 'sesi2-title',
    title: STRINGS.onboardingSesi2Title,
    body: STRINGS.onboardingSesi2Body,
    cta: STRINGS.onboardingSesi2Cta,
  },
  // Sesi 3: "Bagikan ke ortu" — placeholder sampai M6.B
  {
    key: 'sesi3-title',
    title: STRINGS.onboardingSesi3Title,
    body: STRINGS.onboardingSesi3Body,
    cta: STRINGS.onboardingSesi3Cta,
  },
]

export function createOnboardingTour({ store, onComplete, onSkip } = {}) {
  if (!store?.getSetting || !store?.setSetting) {
    throw new Error('OnboardingTour: store with getSetting/setSetting is required')
  }

  let sessionCount = 0
  let shareLinkEnabled = false
  let stepIndex = 0
  let completed = false
  let started = false

  async function loadState() {
    sessionCount = (await store.getSetting('onboardingSessionCount')) ?? 0
    shareLinkEnabled = (await store.getSetting('shareLinkEnabled')) === true
  }

  function getSesiTemplate() {
    // Sesi 3: kalau share link belum enabled, override body ke placeholder.
    if (stepIndex === 2 && !shareLinkEnabled) {
      return {
        ...SESI_TEMPLATE[2],
        body: `${SESI_TEMPLATE[2].body}\n\n⏳ ${STRINGS.onboardingSesi3PlaceholderHint}`,
      }
    }
    return SESI_TEMPLATE[stepIndex]
  }

  function getCurrentStep() {
    return { sesi: stepIndex + 1, ...getSesiTemplate() }
  }

  function isCompleted() {
    return completed
  }

  async function start() {
    if (started) return
    started = true
    await loadState()
    if (sessionCount >= MAX_SESSIONS) {
      completed = true
      onComplete?.({ reason: 'already-completed', sessionCount })
      return
    }
    stepIndex = 0
    trackEvent('onboarding_started', { sessionCount })
  }

  async function next() {
    if (completed) return
    stepIndex += 1
    if (stepIndex >= SESI_TEMPLATE.length) {
      completed = true
      const newCount = sessionCount + 1
      await store.setSetting('onboardingSessionCount', newCount)
      sessionCount = newCount
      trackEvent('onboarding_completed', { sessionCount: newCount })
      onComplete?.({ reason: 'finished-all-sesi', sessionCount: newCount })
    } else {
      trackEvent('onboarding_step_completed', {
        sesi: stepIndex,
        step: SESI_TEMPLATE[stepIndex].key,
      })
    }
  }

  async function skip() {
    if (completed) return
    completed = true
    const newCount = sessionCount + 1
    await store.setSetting('onboardingSessionCount', newCount)
    sessionCount = newCount
    trackEvent('onboarding_skipped', { atSesi: stepIndex + 1 })
    onSkip?.({ atSesi: stepIndex + 1, sessionCount: newCount })
  }

  function destroy() {
    started = false
    completed = false
    stepIndex = 0
  }

  return { start, next, skip, getCurrentStep, isCompleted, destroy }
}
